/**
 * Dashboard - Vista principal de la aplicación
 *
 * Diseño mobile-first con adaptaciones para desktop.
 * Incluye "Modo Día Difícil" para mostrar solo supervivencia.
 */

import { useMemo } from 'react';
import { Calendar, CalendarDays, CalendarRange, CalendarClock, CalendarCheck, Battery, BatteryLow, Sun, Briefcase } from 'lucide-react';
import { useProtocolStore, useUIStore } from '@/store';
import { cn, formatMinutes, getDateLabel, groupByLayer, filterByFrequency } from '@/core/utils';
import { FREQUENCIES, FREQUENCY_ORDER, groupProtocolsByTimeBlock } from '@/core/constants';
import { useTimeBlocks, useCurrentTimeBlock } from '@/core/hooks';
import type { Frequency } from '@/core/types';
import { LayerProgressIndicator } from '@/ui/primitives';
import { ProtocolGroup } from './ProtocolGroup';
import { DailyTimeline } from './DailyTimeline';

const frequencyIcons: Record<Frequency, typeof Calendar> = {
  daily: Calendar,
  weekly: CalendarDays,
  monthly: CalendarRange,
  quarterly: CalendarClock,
  yearly: CalendarCheck,
};

export function Dashboard() {
  const activeFrequency = useProtocolStore((state) => state.activeFrequency);
  const setActiveFrequency = useProtocolStore((state) => state.setActiveFrequency);
  const protocols = useProtocolStore((state) => state.protocols);
  const hardDayMode = useUIStore((state) => state.hardDayMode);
  const toggleHardDayMode = useUIStore((state) => state.toggleHardDayMode);
  const weekendMode = useUIStore((state) => state.weekendMode);
  const toggleWeekendMode = useUIStore((state) => state.toggleWeekendMode);

  // Bloques de tiempo personalizados según preferencias del usuario
  const timeBlocks = useTimeBlocks();
  const currentTimeBlock = useCurrentTimeBlock();

  // Calcular grupos con useMemo para evitar re-renders infinitos
  const allGroups = useMemo(
    () => groupByLayer(filterByFrequency(protocols, activeFrequency)),
    [protocols, activeFrequency]
  );

  // En modo día difícil, solo mostrar supervivencia
  const groups = useMemo(
    () => hardDayMode ? allGroups.filter((g) => g.layer === 'survival') : allGroups,
    [allGroups, hardDayMode]
  );

  // Calcular tiempo total y completado
  const totalTime = useMemo(
    () => groups.reduce((acc, g) => acc + g.totalTime, 0),
    [groups]
  );

  const completedTime = useMemo(
    () =>
      groups.reduce(
        (acc, g) =>
          acc +
          g.protocols
            .filter((p) => p.isCompleted)
            .reduce((a, p) => a + p.timeEstimate, 0),
        0
      ),
    [groups]
  );

  // Grupos por bloque horario para vista diaria
  // En modo fin de semana:
  // - Ocultar protocolos con tag 'trabajo' o 'laboral'
  // - Mostrar protocolos con tag 'finde'
  // En modo laboral:
  // - Ocultar protocolos con tag 'finde'
  // - Mostrar protocolos con tag 'laboral'
  const timeBlockGroups = useMemo(() => {
    const filteredProtocols = weekendMode
      ? protocols.filter((p) => {
          const tags = p.tags || [];
          // En finde: mostrar si tiene tag 'finde' O si no tiene ni 'trabajo' ni 'laboral'
          const hasFinde = tags.includes('finde');
          const hasLaboral = tags.includes('laboral') || tags.includes('trabajo');
          return hasFinde || !hasLaboral;
        })
      : protocols.filter((p) => {
          const tags = p.tags || [];
          // En laboral: ocultar protocolos con tag 'finde'
          return !tags.includes('finde');
        });
    return groupProtocolsByTimeBlock(filteredProtocols, timeBlocks);
  }, [protocols, weekendMode, timeBlocks]);

  // Determinar si usar vista por bloques (solo en diario y sin modo día difícil)
  const useTimeBlockView = activeFrequency === 'daily' && !hardDayMode;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Tabs de frecuencia - stack vertical en móvil muy pequeño, horizontal en móvil+ */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-1 p-1 bg-slate-900 rounded-lg overflow-x-auto scrollbar-hide">
          {FREQUENCY_ORDER.map((freq) => {
            const Icon = frequencyIcons[freq];
            const isActive = freq === activeFrequency;

            return (
              <button
                key={freq}
                onClick={() => setActiveFrequency(freq)}
                className={cn(
                  'flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 rounded-md',
                  'text-xs sm:text-sm font-medium transition-colors whitespace-nowrap',
                  'flex-shrink-0',
                  isActive
                    ? 'bg-slate-800 text-slate-100'
                    : 'text-slate-400 hover:text-slate-200'
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden md:inline">
                  {FREQUENCIES[freq].label}
                </span>
                <span className="md:hidden">{FREQUENCIES[freq].shortLabel}</span>
              </button>
            );
          })}
        </div>

        {/* Modos especiales + Resumen de tiempo */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Botón Modo Fin de Semana (solo visible en vista diaria) */}
          {activeFrequency === 'daily' && (
            <button
              onClick={toggleWeekendMode}
              className={cn(
                'flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all',
                weekendMode
                  ? 'bg-amber-900/50 text-amber-300 border border-amber-700'
                  : 'bg-slate-800/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              )}
              title={weekendMode ? 'Cambiar a horario laboral' : 'Cambiar a horario de fin de semana'}
            >
              {weekendMode ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Briefcase className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Botón Modo Día Difícil */}
          <button
            onClick={toggleHardDayMode}
            className={cn(
              'flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all',
              hardDayMode
                ? 'bg-rose-900/50 text-rose-300 border border-rose-700 shadow-glow-survival'
                : 'bg-slate-800/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            )}
            title={hardDayMode ? 'Desactivar modo día difícil' : 'Activar modo día difícil (solo supervivencia)'}
          >
            {hardDayMode ? (
              <>
                <BatteryLow className="w-4 h-4" />
                <span className="hidden sm:inline">Difícil</span>
              </>
            ) : (
              <>
                <Battery className="w-4 h-4" />
                <span className="hidden sm:inline">Normal</span>
              </>
            )}
          </button>

          {/* Resumen de tiempo */}
          <div className="text-right flex-shrink-0">
            <p className="text-sm text-slate-400">
              {formatMinutes(completedTime)} / {formatMinutes(totalTime)}
            </p>
            <p className="text-xs text-slate-600">{getDateLabel(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Banner de Modo Fin de Semana */}
      {weekendMode && activeFrequency === 'daily' && !hardDayMode && (
        <div className="bg-amber-900/30 border border-amber-800 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <Sun className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-200">Modo Fin de Semana</p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                Sin trabajo. Horarios relajados, estudio profundo y tiempo para ti.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Modo Día Difícil */}
      {hardDayMode && (
        <div className="bg-rose-900/30 border border-rose-800 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <BatteryLow className="w-5 h-5 text-rose-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-rose-200">Modo Día Difícil Activo</p>
              <p className="text-xs text-rose-400/80 mt-0.5">
                Solo se muestra la capa de Supervivencia. Completa lo esencial, el resto es bonus.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Barra de progreso por capas - siempre muestra las 3 capas */}
      <div className="space-y-2">
        <div className="flex justify-between text-2xs sm:text-xs text-slate-500">
          <span>Supervivencia</span>
          <span>Mantenimiento</span>
          <span>Crecimiento</span>
        </div>
        <LayerProgressIndicator
          survival={
            allGroups.find((g) => g.layer === 'survival')?.totalCount
              ? ((allGroups.find((g) => g.layer === 'survival')?.completedCount ?? 0) /
                  (allGroups.find((g) => g.layer === 'survival')?.totalCount ?? 1)) *
                100
              : 0
          }
          maintenance={
            allGroups.find((g) => g.layer === 'maintenance')?.totalCount
              ? ((allGroups.find((g) => g.layer === 'maintenance')?.completedCount ?? 0) /
                  (allGroups.find((g) => g.layer === 'maintenance')?.totalCount ?? 1)) *
                100
              : 0
          }
          growth={
            allGroups.find((g) => g.layer === 'growth')?.totalCount
              ? ((allGroups.find((g) => g.layer === 'growth')?.completedCount ?? 0) /
                  (allGroups.find((g) => g.layer === 'growth')?.totalCount ?? 1)) *
                100
              : 0
          }
          size="md"
        />
      </div>

      {/* Vista timeline diaria (solo en diario) */}
      {useTimeBlockView && (
        <DailyTimeline
          groups={timeBlockGroups}
          currentTimeBlock={currentTimeBlock}
        />
      )}

      {/* Vista por capas (semanal, mensual, trimestral o modo día difícil) */}
      {!useTimeBlockView && (
        <div className="space-y-6">
          {groups.map((group) => {
            // Determinar si la capa está desbloqueada
            // Para frecuencias no diarias: siempre desbloqueado (no tiene sentido bloquear por capas)
            // Para modo día difícil: aplicar lógica de capas
            let isUnlocked = true;

            if (hardDayMode) {
              // Solo en modo día difícil aplicamos el bloqueo por capas
              const layerIndex = ['survival', 'maintenance', 'growth'].indexOf(
                group.layer
              );

              if (layerIndex > 0) {
                const previousLayer = ['survival', 'maintenance', 'growth'][
                  layerIndex - 1
                ] as 'survival' | 'maintenance' | 'growth';
                const previousGroup = groups.find((g) => g.layer === previousLayer);
                // Si la capa anterior no existe, está desbloqueado
                // Si existe, debe estar completa
                isUnlocked = previousGroup ? previousGroup.isFullyCompleted : true;
              }
            }

            return (
              <ProtocolGroup
                key={group.layer}
                group={group}
                isUnlocked={isUnlocked}
              />
            );
          })}
        </div>
      )}

      {/* Mensaje si no hay protocolos */}
      {(useTimeBlockView ? timeBlockGroups.length === 0 : groups.every((g) => g.protocols.length === 0)) && (
        <div className="text-center py-12">
          <p className="text-slate-500">
            No hay protocolos configurados para esta frecuencia.
          </p>
        </div>
      )}
    </div>
  );
}
